import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Notification engine contracts (ADR-0048).
 *
 * Event-driven, channel-pluggable, admin-configurable. Six concept
 * objects: Event, Channel, Template, Rule, Audience, Notification.
 *
 * The publishing modules (leads, customers, ...) call exactly ONE
 * method - `notifications.publish(eventCode, payload)` - and don't
 * touch any of the contracts below. Everything here serves the admin
 * UI + the internal engine.
 */

// ===== Enums =====

/**
 * Channel kinds.
 *
 * The first five map 1:1 to the `NotificationChannelKind` Prisma enum and to
 * rows in `notification_channels`. `DOCUMENT` deliberately does NOT: it exists
 * only in this TypeScript enum, is never written to the database, and never
 * appears in a `notification_channels` row.
 *
 * Why the asymmetry. A document channel does not deliver anything - the PDF is
 * drawn in the traveller's browser when they press Download, and the template
 * only supplies the wording. Making it a real enum value would mean a migration
 * on a database every branch shares, and any branch whose Prisma client predates
 * that migration would hit P2023 the moment it read the channels list. A
 * TypeScript-only kind buys the same admin experience with none of that: the
 * template row stores `channel_code = 'pdf'`, which is a plain `TEXT` column, so
 * an older branch reading `notification_templates` sees an unfamiliar string and
 * carries on.
 *
 * See NOTIFICATION_VIRTUAL_CHANNELS below for the channel descriptor itself.
 */
export const NOTIFICATION_CHANNEL_KINDS = [
  'EMAIL',
  'IN_APP',
  'SMS',
  'WHATSAPP',
  'PUSH',
  'DOCUMENT',
] as const;
export const NotificationChannelKindSchema = z.enum(NOTIFICATION_CHANNEL_KINDS);
export type NotificationChannelKind = z.infer<typeof NotificationChannelKindSchema>;

export const NOTIFICATION_AUDIENCE_KINDS = [
  'ROLES',
  'USERS',
  'RESOLVER',
  'EVENT_PAYLOAD_FIELD',
  'BRANCH',
] as const;
export const NotificationAudienceKindSchema = z.enum(NOTIFICATION_AUDIENCE_KINDS);
export type NotificationAudienceKind = z.infer<typeof NotificationAudienceKindSchema>;

export const NOTIFICATION_STATUSES = ['PENDING', 'QUEUED', 'SENT', 'FAILED', 'SKIPPED'] as const;
export const NotificationStatusSchema = z.enum(NOTIFICATION_STATUSES);
export type NotificationStatus = z.infer<typeof NotificationStatusSchema>;

export const NOTIFICATION_REMINDER_STATUSES = ['PENDING', 'DISPATCHED', 'CANCELED'] as const;
export const NotificationReminderStatusSchema = z.enum(NOTIFICATION_REMINDER_STATUSES);
export type NotificationReminderStatus = z.infer<typeof NotificationReminderStatusSchema>;

// ===== Audience spec discriminated union =====

/// Spec shape per kind. Stored as JSON on `notification_audiences.spec`.
/// The dispatcher's RecipientResolver pattern-matches on `kind` and
/// expects the matching spec shape. Bad spec -> resolver throws ->
/// notification row marked FAILED.
export const NotificationAudienceSpecSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ROLES'), roles: z.array(z.string()).min(1) }),
  z.object({ kind: z.literal('USERS'), userIds: z.array(UuidSchema).min(1) }),
  z.object({ kind: z.literal('RESOLVER'), resolverKey: z.string().min(1) }),
  z.object({
    kind: z.literal('EVENT_PAYLOAD_FIELD'),
    field: z
      .string()
      .min(1)
      .describe(
        'Dot-path into the event payload that yields a UUID or string[] of UUIDs (e.g. "assignedAgentId").',
      ),
  }),
  z.object({ kind: z.literal('BRANCH'), branchIds: z.array(UuidSchema).min(1) }),
]);
export type NotificationAudienceSpec = z.infer<typeof NotificationAudienceSpecSchema>;

// ===== Rule conditions DSL =====

/// Tiny JSON DSL for filtering events at rule-match time. Empty / null
/// = match always. Supports `eq`, `ne`, `in`, `nin`, `gt`, `gte`,
/// `lt`, `lte`, plus boolean `and` / `or` / `not`. Evaluated against
/// the event payload by RuleService. Documented + sample-tested.
const ConditionLeafSchema = z.object({
  field: z.string().min(1),
  op: z.enum(['eq', 'ne', 'in', 'nin', 'gt', 'gte', 'lt', 'lte']),
  value: z.unknown(),
});
export type NotificationRuleConditionLeaf = z.infer<typeof ConditionLeafSchema>;

export const NotificationRuleConditionsSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    ConditionLeafSchema,
    z.object({ and: z.array(NotificationRuleConditionsSchema).min(1) }),
    z.object({ or: z.array(NotificationRuleConditionsSchema).min(1) }),
    z.object({ not: NotificationRuleConditionsSchema }),
  ]),
);

// ===== Event =====

export const NotificationEventItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  module: z.string(),
  payloadSchema: z.unknown().nullable(),
  samplePayload: z.unknown().nullable(),
  isMandatory: z.boolean(),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type NotificationEventItem = z.infer<typeof NotificationEventItemSchema>;

export const NotificationEventListQuerySchema = PaginationQuerySchema.extend({
  module: z.string().optional(),
  isActive: BooleanQueryParamSchema.optional(),
});
export type NotificationEventListQuery = z.infer<typeof NotificationEventListQuerySchema>;

export const NotificationEventListResponseSchema = paginatedResponseSchema(
  NotificationEventItemSchema,
);
export type NotificationEventListResponse = z.infer<typeof NotificationEventListResponseSchema>;

export const UpdateNotificationEventRequestSchema = z.object({
  /// `code` and `module` are immutable - they're the contract the
  /// publishing module relies on. Admins can edit display fields only.
  name: z.string().trim().min(1).max(160).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  samplePayload: z.unknown().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateNotificationEventRequest = z.infer<typeof UpdateNotificationEventRequestSchema>;

/**
 * Admin-create payload (ADR-0049 follow-up). Event codes are
 * usually defined in code (notifications-event-seeds.service.ts), but
 * the admin can also register a new code from the UI for ad-hoc
 * integrations - a webhook flow, a manual ops notification, etc.
 *
 * The code becomes the contract: someone (a publish() call in code,
 * a /publish escape-hatch POST, or the outbox subscriber) must
 * eventually fire this code or the event sits dead in the catalog.
 *
 * Constraints:
 *   - code: snake_dot.case, must not already exist.
 *   - module: free-form label for grouping ('custom', 'webhooks', etc.).
 *   - isMandatory: defaults false. Mandatory events bypass per-user
 *     opt-out but can't be deactivated, so flag carefully.
 */
export const CreateNotificationEventRequestSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, {
      message: 'Use snake_dot.case, e.g. lead.followup.due',
    }),
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(2000).nullable().optional(),
  module: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[a-z][a-z0-9_]*$/, { message: 'lowercase snake_case' }),
  samplePayload: z.unknown().optional(),
  payloadSchema: z.unknown().optional(),
  isMandatory: z.boolean().optional(),
  isActive: z.boolean().optional(),
});
export type CreateNotificationEventRequest = z.infer<typeof CreateNotificationEventRequestSchema>;

// ===== Channel =====

export const NotificationChannelItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  kind: NotificationChannelKindSchema,
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  configSecretRef: z.string().nullable(),
  rateLimitPerSec: z.number().int().positive(),
  createdAt: IsoDateTimeSchema,
});
export type NotificationChannelItem = z.infer<typeof NotificationChannelItemSchema>;

export const NotificationChannelListResponseSchema = z.object({
  data: z.array(NotificationChannelItemSchema),
});
export type NotificationChannelListResponse = z.infer<typeof NotificationChannelListResponseSchema>;

/**
 * Channel code for the PDF document channel. Not a row in
 * `notification_channels` - see NOTIFICATION_CHANNEL_KINDS.
 */
export const NOTIFICATION_PDF_CHANNEL_CODE = 'pdf';

/**
 * Channels that exist in code but not in `notification_channels`.
 *
 * Read by the API (to register the channel implementation) and by the admin
 * Templates tab (to offer it in the channel picker), so both agree on one
 * spelling. The Channels tab is intentionally left alone: it manages delivery
 * transports with credentials and rate limits, none of which a document has.
 */
export const NOTIFICATION_VIRTUAL_CHANNELS = [
  {
    code: NOTIFICATION_PDF_CHANNEL_CODE,
    kind: 'DOCUMENT',
    name: 'PDF document',
    description:
      'Wording for a downloadable PDF - the flight e-ticket, the hotel voucher and their cancellation records. Rendered in the browser on demand, so nothing is delivered and nothing is queued.',
  },
] as const satisfies ReadonlyArray<{
  code: string;
  kind: NotificationChannelKind;
  name: string;
  description: string;
}>;

export const UpdateNotificationChannelRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  isActive: z.boolean().optional(),
  configSecretRef: z.string().trim().max(120).nullable().optional(),
  rateLimitPerSec: z.number().int().min(1).max(10_000).optional(),
});
export type UpdateNotificationChannelRequest = z.infer<
  typeof UpdateNotificationChannelRequestSchema
>;

// ===== Template =====

/// Provider-side send config for a WhatsApp template.
///
/// Meta will not let anyone send free-form WhatsApp business messages:
/// the body must be a template they approved, and DoubleTick addresses
/// those templates by TEMPLATE NAME with a POSITIONAL parameter array.
/// So the operator-authored half of a WhatsApp "template" is a
/// reference plus a mapping - "template `utazzo_itinerary_share`, and
/// slot {{1}} is our {{customerFirstName}}" - not prose.
///
/// Every string here goes through the engine's normal `{{var}}`
/// interpolation against the event payload before it is handed to the
/// provider, so `params: ['{{customerFirstName}}']` renders to
/// `['Rahul']`.
export const WhatsAppTemplateProviderConfigSchema = z.object({
  /// 'doubletick' for anything new; 'aisensy' still parses so template
  /// rows saved before the 2026-09 provider migration stay valid.
  provider: z.enum(['doubletick', 'aisensy']),
  /// Meta-approved template name exactly as it appears in DoubleTick's
  /// Templates list (field kept as campaignName for continuity with
  /// stored rows). The template must be APPROVED or the send fails.
  campaignName: z.string().trim().min(1).max(200),
  /// Ordered fill for the template's {{1}}, {{2}}, ... placeholders.
  /// Meta rejects the send when the length doesn't match the approved
  /// template exactly, so order and count are load-bearing.
  params: z.array(z.string()).max(30).default([]),
  /// Header media for IMAGE / DOCUMENT / VIDEO templates. The URL must
  /// be publicly reachable - WhatsApp fetches it server-side and
  /// rejects anything it can't GET.
  media: z
    .object({
      url: z.string().trim().min(1),
      filename: z.string().trim().max(200).optional(),
    })
    .nullable()
    .optional(),
  /// Dynamic URL-button suffixes, when the approved template has a
  /// button whose URL ends in a variable.
  buttonParams: z.array(z.string()).max(10).optional(),
});
export type WhatsAppTemplateProviderConfig = z.infer<typeof WhatsAppTemplateProviderConfigSchema>;

/**
 * Operator-authored wording for a downloadable PDF.
 *
 * The PDF is drawn, not typeset from HTML: a vector document whose numbers stay
 * sharp when a receptionist zooms in, assembled by the renderer in
 * `apps/website/lib/tickets/ticket-pdf.ts` from facts the booking already
 * carries. What an operator can usefully change is therefore not the layout but
 * the WORDS AROUND the facts - what the document calls itself, what each block
 * of rows is headed, and the small print at the foot. Those are the fields
 * below, and they are the whole of it.
 *
 * Every string goes through the engine's `{{var}}` interpolation before it
 * reaches the renderer, against the same variables the matching email template
 * sees (`{{reference}}`, `{{pnr}}`, `{{hotelName}}`, ...). An unresolvable
 * placeholder renders EMPTY here rather than verbatim: this text is read by a
 * traveller at a check-in desk, where a literal `{{pnr}}` looks like a broken
 * document rather than a QA hint.
 */
export const PdfDocumentTemplateConfigSchema = z.object({
  provider: z.literal('pdf'),
  /// The strip above the title: "E-ticket", "Hotel voucher".
  kicker: z.string().trim().min(1).max(60),
  /// PDF title and the name shown in the reader's tab.
  documentTitle: z.string().trim().min(1).max(200),
  /// Prefix for the downloaded file name. The reference and `.pdf` are
  /// appended by the renderer, so this is the human-readable half only.
  fileNamePrefix: z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(
      /^[a-z0-9-]+$/,
      'Use lowercase letters, digits and hyphens only - this becomes a file name.',
    ),
  /// Loud banner across the top, for a document that must not be mistaken for
  /// a live one. Null on an ordinary ticket or voucher.
  statusLabel: z.string().trim().max(60).nullable().optional(),
  /// One line under the banner - when, and what it means for the reader.
  statusDetail: z.string().trim().max(200).nullable().optional(),
  /// Repeated diagonally across every page. Only for a document that must
  /// never pass as valid; a banner alone is missed on page two.
  watermark: z.string().trim().max(40).nullable().optional(),
  /// Section heading overrides, keyed by the renderer's section key
  /// (`flights`, `baggage`, `passengers`, `fare`, `stay`, `rooms`, `policy`,
  /// `cancellation`). An absent key keeps the renderer's built-in heading, so
  /// an operator only types the ones they want to change.
  headings: z.record(z.string(), z.string().trim().max(120)).default({}),
  /// Extra paragraphs printed under a section's rows, keyed the same way.
  notes: z.record(z.string(), z.array(z.string().trim().max(600)).max(6)).default({}),
  /// Small print at the foot of the last page - ID requirements, check-in
  /// cut-offs, the agency disclaimer.
  footNotes: z.array(z.string().trim().max(600)).max(12).default([]),
});
export type PdfDocumentTemplateConfig = z.infer<typeof PdfDocumentTemplateConfigSchema>;

/// Discriminated on `provider` so a second provider-backed channel
/// (an SMS gateway, a push service) can join without reshaping the
/// column or the admin form.
export const NotificationTemplateProviderConfigSchema = z.discriminatedUnion('provider', [
  WhatsAppTemplateProviderConfigSchema,
  PdfDocumentTemplateConfigSchema,
]);
export type NotificationTemplateProviderConfig = z.infer<
  typeof NotificationTemplateProviderConfigSchema
>;

export const NotificationTemplateItemSchema = z.object({
  id: UuidSchema,
  eventCode: z.string(),
  channelCode: z.string(),
  locale: z.string(),
  name: z.string(),
  subject: z.string().nullable(),
  bodyHtml: z.string().nullable(),
  bodyText: z.string().nullable(),
  bodyMarkdown: z.string().nullable(),
  /// Set only for provider-backed channels (WhatsApp). Null everywhere
  /// else. Loose `unknown` on the read path so a row written by a
  /// newer provider shape can't 500 the list endpoint; the write path
  /// validates strictly.
  providerConfig: z.unknown().nullable().optional(),
  version: z.number().int().positive(),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type NotificationTemplateItem = z.infer<typeof NotificationTemplateItemSchema>;

export const NotificationTemplateListQuerySchema = PaginationQuerySchema.extend({
  eventCode: z.string().optional(),
  channelCode: z.string().optional(),
  locale: z.string().optional(),
  isActive: BooleanQueryParamSchema.optional(),
});
export type NotificationTemplateListQuery = z.infer<typeof NotificationTemplateListQuerySchema>;

export const NotificationTemplateListResponseSchema = paginatedResponseSchema(
  NotificationTemplateItemSchema,
);
export type NotificationTemplateListResponse = z.infer<
  typeof NotificationTemplateListResponseSchema
>;

export const CreateNotificationTemplateRequestSchema = z.object({
  eventCode: z.string().trim().min(1),
  channelCode: z.string().trim().min(1),
  locale: z.string().trim().min(1).default('en-IN'),
  name: z.string().trim().min(1).max(160),
  subject: z.string().trim().max(500).nullable().optional(),
  bodyHtml: z.string().nullable().optional(),
  bodyText: z.string().nullable().optional(),
  bodyMarkdown: z.string().nullable().optional(),
  /// Required in practice for `channelCode: 'whatsapp'` - a WhatsApp
  /// template with no campaign has nothing to send. Enforced in the
  /// admin service (which knows the channel's kind) rather than here,
  /// so the check lives next to the channel lookup instead of being
  /// duplicated as a channel-code string match in the schema.
  providerConfig: NotificationTemplateProviderConfigSchema.nullable().optional(),
});
export type CreateNotificationTemplateRequest = z.infer<
  typeof CreateNotificationTemplateRequestSchema
>;

export const UpdateNotificationTemplateRequestSchema =
  CreateNotificationTemplateRequestSchema.partial().extend({
    isActive: z.boolean().optional(),
  });
export type UpdateNotificationTemplateRequest = z.infer<
  typeof UpdateNotificationTemplateRequestSchema
>;

/// Render-preview request - rendered against operator-supplied variables.
/// Returns the rendered subject + bodies without writing a notification
/// row. Used by the admin Template editor's live preview pane.
export const PreviewNotificationTemplateRequestSchema = z.object({
  variables: z.record(z.string(), z.unknown()).default({}),
});
export type PreviewNotificationTemplateRequest = z.infer<
  typeof PreviewNotificationTemplateRequestSchema
>;

export const PreviewNotificationTemplateResponseSchema = z.object({
  subject: z.string().nullable(),
  bodyHtml: z.string().nullable(),
  bodyText: z.string().nullable(),
  bodyMarkdown: z.string().nullable(),
  /// Provider config with every `{{var}}` already resolved against the
  /// supplied variables. Lets the WhatsApp editor show the operator the
  /// literal parameter array that would reach DoubleTick - the single most
  /// common failure is a slot mapped to a variable the event doesn't
  /// carry, which is invisible until you see the rendered values.
  providerConfig: z.unknown().nullable().optional(),
});
export type PreviewNotificationTemplateResponse = z.infer<
  typeof PreviewNotificationTemplateResponseSchema
>;

// ===== Audience =====

export const NotificationAudienceItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  kind: NotificationAudienceKindSchema,
  spec: NotificationAudienceSpecSchema,
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type NotificationAudienceItem = z.infer<typeof NotificationAudienceItemSchema>;

export const NotificationAudienceListResponseSchema = paginatedResponseSchema(
  NotificationAudienceItemSchema,
);
export type NotificationAudienceListResponse = z.infer<
  typeof NotificationAudienceListResponseSchema
>;

export const NotificationAudienceListQuerySchema = PaginationQuerySchema.extend({
  kind: NotificationAudienceKindSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
});
export type NotificationAudienceListQuery = z.infer<typeof NotificationAudienceListQuerySchema>;

export const CreateNotificationAudienceRequestSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[a-z][a-z0-9_]*$/, 'lowercase snake_case'),
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(500).nullable().optional(),
  spec: NotificationAudienceSpecSchema,
});
export type CreateNotificationAudienceRequest = z.infer<
  typeof CreateNotificationAudienceRequestSchema
>;

export const UpdateNotificationAudienceRequestSchema =
  CreateNotificationAudienceRequestSchema.partial().extend({
    isActive: z.boolean().optional(),
  });
export type UpdateNotificationAudienceRequest = z.infer<
  typeof UpdateNotificationAudienceRequestSchema
>;

// ===== Rule =====

export const NotificationRuleItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  eventCode: z.string(),
  channelCode: z.string(),
  templateId: UuidSchema.nullable(),
  audienceId: UuidSchema,
  conditions: z.unknown().nullable(),
  throttleSeconds: z.number().int().nullable(),
  quietHoursStart: z.number().int().min(0).max(23).nullable(),
  quietHoursEnd: z.number().int().min(0).max(23).nullable(),
  priority: z.number().int(),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type NotificationRuleItem = z.infer<typeof NotificationRuleItemSchema>;

export const NotificationRuleListQuerySchema = PaginationQuerySchema.extend({
  eventCode: z.string().optional(),
  channelCode: z.string().optional(),
  isActive: BooleanQueryParamSchema.optional(),
});
export type NotificationRuleListQuery = z.infer<typeof NotificationRuleListQuerySchema>;

export const NotificationRuleListResponseSchema = paginatedResponseSchema(
  NotificationRuleItemSchema,
);
export type NotificationRuleListResponse = z.infer<typeof NotificationRuleListResponseSchema>;

export const CreateNotificationRuleRequestSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[a-z][a-z0-9_]*$/, 'lowercase snake_case'),
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(500).nullable().optional(),
  eventCode: z.string().trim().min(1),
  channelCode: z.string().trim().min(1),
  templateId: UuidSchema.nullable().optional(),
  audienceId: UuidSchema,
  conditions: NotificationRuleConditionsSchema.nullable().optional(),
  throttleSeconds: z
    .number()
    .int()
    .min(0)
    .max(86_400 * 30)
    .nullable()
    .optional(),
  quietHoursStart: z.number().int().min(0).max(23).nullable().optional(),
  quietHoursEnd: z.number().int().min(0).max(23).nullable().optional(),
  priority: z.number().int().min(0).max(1000).optional(),
});
export type CreateNotificationRuleRequest = z.infer<typeof CreateNotificationRuleRequestSchema>;

export const UpdateNotificationRuleRequestSchema =
  CreateNotificationRuleRequestSchema.partial().extend({
    isActive: z.boolean().optional(),
  });
export type UpdateNotificationRuleRequest = z.infer<typeof UpdateNotificationRuleRequestSchema>;

/// Dry-run a rule against a sample payload. Returns the resolved
/// recipients WITHOUT sending. Lets admins sanity-check their audience
/// + condition combos before the rule goes live.
export const DryRunNotificationRuleRequestSchema = z.object({
  payload: z.record(z.string(), z.unknown()),
});
export type DryRunNotificationRuleRequest = z.infer<typeof DryRunNotificationRuleRequestSchema>;

export const DryRunNotificationRuleResponseSchema = z.object({
  matched: z.boolean(),
  reason: z.string().nullable(),
  recipientCount: z.number().int().nonnegative(),
  recipientUserIds: z.array(UuidSchema),
});
export type DryRunNotificationRuleResponse = z.infer<typeof DryRunNotificationRuleResponseSchema>;

// ===== Notification (delivery row) =====

export const NotificationItemSchema = z.object({
  id: UuidSchema,
  eventCode: z.string(),
  ruleId: UuidSchema.nullable(),
  templateId: UuidSchema.nullable(),
  templateVersion: z.number().int().nullable(),
  channelCode: z.string(),
  recipientUserId: UuidSchema.nullable(),
  recipientAddress: z.string().nullable(),
  subject: z.string().nullable(),
  bodyHtml: z.string().nullable(),
  bodyText: z.string().nullable(),
  status: NotificationStatusSchema,
  attempts: z.number().int(),
  /// How many times this in-app notification type has fired while unread.
  /// Repeats of the same (recipient, event) collapse onto one row and bump
  /// this. 1 for first occurrence / non-coalesced channels.
  occurrenceCount: z.number().int().min(1).default(1),
  errorMessage: z.string().nullable(),
  scheduledAt: IsoDateTimeSchema.nullable(),
  queuedAt: IsoDateTimeSchema.nullable(),
  sentAt: IsoDateTimeSchema.nullable(),
  readAt: IsoDateTimeSchema.nullable(),
  skipReason: z.string().nullable(),
  correlationId: UuidSchema.nullable(),
  /// Free-form correlation tags (ADR-0049). Carries `leadId`,
  /// `customerId`, `bookingId` etc. so the History view can answer
  /// "all notifications for lead X" via one GIN-indexed query.
  tags: z.record(z.string(), z.string()).nullable().optional(),
  /// Optional enrichment for lead-tagged notifications. The user-
  /// facing "my notifications" inbox surfaces this so the row shows
  /// the lead title + customer name + stage chip at a glance. Other
  /// endpoints (history, bell) leave it `null` to keep the response
  /// light; the schema's `.nullable().optional()` keeps the contract
  /// stable across both shapes.
  lead: z
    .object({
      id: UuidSchema,
      reference: z.string(),
      title: z.string(),
      createdAt: IsoDateTimeSchema,
      currentStatus: z.object({ name: z.string(), color: z.string().nullable() }).nullable(),
      customer: z.object({ id: UuidSchema, displayName: z.string() }).nullable(),
    })
    .nullable()
    .optional(),
  createdAt: IsoDateTimeSchema,
});
export type NotificationItem = z.infer<typeof NotificationItemSchema>;

export const NotificationListQuerySchema = PaginationQuerySchema.extend({
  eventCode: z.string().optional(),
  channelCode: z.string().optional(),
  status: NotificationStatusSchema.optional(),
  recipientUserId: UuidSchema.optional(),
  correlationId: UuidSchema.optional(),
  ruleId: UuidSchema.optional(),
  /// YYYY-MM-DD inclusive lower bound on createdAt.
  createdAtFrom: z.iso.date().optional(),
  createdAtTo: z.iso.date().optional(),
  /// Tag filter (ADR-0049). Returns rows where `tags @> {tagKey: tagValue}`.
  /// Common pair: tagKey='leadId', tagValue=<lead uuid> -> all
  /// notifications fanned out by any rule for that lead.
  tagKey: z.string().optional(),
  tagValue: z.string().optional(),
});
export type NotificationListQuery = z.infer<typeof NotificationListQuerySchema>;

export const NotificationListResponseSchema = paginatedResponseSchema(NotificationItemSchema);
export type NotificationListResponse = z.infer<typeof NotificationListResponseSchema>;

// ===== Preference =====

export const NotificationPreferenceItemSchema = z.object({
  id: UuidSchema,
  userId: UuidSchema,
  eventCode: z.string().nullable(),
  ruleId: UuidSchema.nullable(),
  channelCode: z.string().nullable(),
  enabled: z.boolean(),
  updatedAt: IsoDateTimeSchema,
});
export type NotificationPreferenceItem = z.infer<typeof NotificationPreferenceItemSchema>;

export const UpsertNotificationPreferenceRequestSchema = z
  .object({
    eventCode: z.string().nullable().optional(),
    ruleId: UuidSchema.nullable().optional(),
    channelCode: z.string().nullable().optional(),
    enabled: z.boolean(),
  })
  .refine(
    (v) => Boolean(v.eventCode) !== Boolean(v.ruleId),
    'Provide exactly one of eventCode or ruleId',
  );
export type UpsertNotificationPreferenceRequest = z.infer<
  typeof UpsertNotificationPreferenceRequestSchema
>;

// ===== Reminder =====

export const NotificationReminderItemSchema = z.object({
  id: UuidSchema,
  eventCode: z.string(),
  payload: z.unknown(),
  dueAt: IsoDateTimeSchema,
  status: NotificationReminderStatusSchema,
  correlationId: UuidSchema.nullable(),
  dispatchedAt: IsoDateTimeSchema.nullable(),
  canceledAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type NotificationReminderItem = z.infer<typeof NotificationReminderItemSchema>;

// ===== Test send + Publish (admin-only escape hatches) =====

/// Send one notification immediately, bypassing rules + audience.
/// Lets admins QA a template against their own address. Writes a
/// regular `notifications` row so the History tab shows it.
export const TestSendNotificationRequestSchema = z.object({
  templateId: UuidSchema,
  recipientAddress: z.string().trim().min(1).optional(),
  recipientUserId: UuidSchema.optional(),
  variables: z.record(z.string(), z.unknown()).default({}),
});
export type TestSendNotificationRequest = z.infer<typeof TestSendNotificationRequestSchema>;

/// Outcome of a test send.
///
/// Carries the FINAL status, not just an id, because a permanent
/// channel failure (a WhatsApp campaign that isn't Live, a bad API
/// key) is recorded terminally rather than thrown - so a 200 here
/// does NOT mean the message went out. The UI has to read `status`
/// to tell the operator the truth.
export const TestSendNotificationResponseSchema = z.object({
  id: UuidSchema,
  status: NotificationStatusSchema,
  /// Populated when status is SKIPPED.
  skipReason: z.string().nullable().optional(),
  /// Populated when status is FAILED - the provider's own error text.
  errorMessage: z.string().nullable().optional(),
  /// Provider-side id when the send was accepted.
  providerMessageId: z.string().nullable().optional(),
});
export type TestSendNotificationResponse = z.infer<typeof TestSendNotificationResponseSchema>;

/// Force-fire an event without an underlying business action. Used
/// for E2E testing the rules + audience + channel chain. ADMIN only.
export const PublishNotificationRequestSchema = z.object({
  eventCode: z.string().trim().min(1),
  payload: z.record(z.string(), z.unknown()),
  scheduledAt: IsoDateTimeSchema.optional(),
  /// Correlation tags (ADR-0049). Persisted on every notification row
  /// fanned out from this event. Convention: `leadId` / `customerId`
  /// for Lead flow, `bookingId` for booking flow, etc.
  tags: z.record(z.string(), z.string()).optional(),
});
export type PublishNotificationRequest = z.infer<typeof PublishNotificationRequestSchema>;

// ===== In-app inbox (polling-based, no websocket) =====

/// What the SPA polls every 30 s for the bell badge. Returns the most
/// recent N in-app notifications for the current user plus an unread
/// count for the badge. Cheap query: indexed on
/// (recipient_user_id, created_at desc).
export const MyNotificationsInboxQuerySchema = z.object({
  /// How many rows to return. Defaults to 20. Capped at 100.
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /// Optional `before` cursor (createdAt ISO timestamp). Used by the
  /// "load older" pagination on the dropdown.
  before: IsoDateTimeSchema.optional(),
});
export type MyNotificationsInboxQuery = z.infer<typeof MyNotificationsInboxQuerySchema>;

export const MyNotificationsInboxResponseSchema = z.object({
  unreadCount: z.number().int().nonnegative(),
  data: z.array(NotificationItemSchema),
});
export type MyNotificationsInboxResponse = z.infer<typeof MyNotificationsInboxResponseSchema>;

/// PATCH body for marking a single row read. Empty body marks read NOW.
export const MarkNotificationReadRequestSchema = z.object({});
export type MarkNotificationReadRequest = z.infer<typeof MarkNotificationReadRequestSchema>;

/// Mark every unread in-app row for the current user as read in one
/// shot. Used by the "Mark all as read" dropdown button. Empty body.
export const MarkAllNotificationsReadRequestSchema = z.object({});
export type MarkAllNotificationsReadRequest = z.infer<typeof MarkAllNotificationsReadRequestSchema>;

export const MarkAllNotificationsReadResponseSchema = z.object({
  markedRead: z.number().int().nonnegative(),
});
export type MarkAllNotificationsReadResponse = z.infer<
  typeof MarkAllNotificationsReadResponseSchema
>;

// ===== Email logs (legacy EmailDispatcher rows, surfaced for visibility) =====

/// Read-only view of `email_logs` rows (ADR-0049). The legacy
/// EmailDispatcher remains the canonical writer for auth flows; the
/// notification engine's History tab reads this table side-by-side
/// with `notifications` so an admin can see every email + every
/// engine-driven notification in one place.
export const EmailLogStatusSchema = z.enum(['PENDING', 'SENT', 'FAILED']);
export type EmailLogStatus = z.infer<typeof EmailLogStatusSchema>;

export const EmailLogItemSchema = z.object({
  id: UuidSchema,
  templateCode: z.string(),
  templateId: UuidSchema.nullable(),
  toAddress: z.string(),
  ccAddress: z.string().nullable(),
  subject: z.string(),
  status: EmailLogStatusSchema,
  errorMessage: z.string().nullable(),
  errorAttempts: z.number().int(),
  sentAt: IsoDateTimeSchema.nullable(),
  userId: UuidSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type EmailLogItem = z.infer<typeof EmailLogItemSchema>;

export const EmailLogListQuerySchema = PaginationQuerySchema.extend({
  status: EmailLogStatusSchema.optional(),
  templateCode: z.string().optional(),
  toAddress: z.string().optional(),
  userId: UuidSchema.optional(),
});
export type EmailLogListQuery = z.infer<typeof EmailLogListQuerySchema>;

export const EmailLogListResponseSchema = paginatedResponseSchema(EmailLogItemSchema);
export type EmailLogListResponse = z.infer<typeof EmailLogListResponseSchema>;

// ===== Channel config (SMTP secrets) =====

/// Stable identifiers for the secrets a channel reads from the
/// SecretsManager. The notification engine's "Channel config" drawer
/// uses these labels + keys to render a focused editor instead of
/// asking the operator to remember the underlying `email.smtp.*` keys.
export const NotificationChannelConfigFieldSchema = z.object({
  /// SecretsManager key (e.g. "email.smtp.host"). Immutable.
  key: z.string(),
  /// Operator-facing label.
  label: z.string(),
  /// Hint text shown beneath the input.
  hint: z.string().nullable(),
  /// Whether the value is sensitive (password-style). Sensitive values
  /// never round-trip via this endpoint; the response carries
  /// `valuePreview` only and full reveal goes via the Secrets module.
  sensitive: z.boolean(),
  /// Whether the secret row is currently present in the store.
  present: z.boolean(),
  /// Non-sensitive: the literal value. Sensitive: '***' (masked).
  /// Null when `present=false`.
  value: z.string().nullable(),
});
export type NotificationChannelConfigField = z.infer<typeof NotificationChannelConfigFieldSchema>;

export const NotificationChannelConfigResponseSchema = z.object({
  channelCode: z.string(),
  fields: z.array(NotificationChannelConfigFieldSchema),
});
export type NotificationChannelConfigResponse = z.infer<
  typeof NotificationChannelConfigResponseSchema
>;

/// PATCH body for upserting one or more channel config fields. Each
/// entry's `value` is the new plaintext; empty string deletes the
/// secret row (sets the field back to "unconfigured"). Sensitive
/// fields can be SET via this endpoint but never READ (round-trip
/// always returns '***'); the Secrets manager remains the canonical
/// reveal surface for audit reasons.
export const UpdateNotificationChannelConfigRequestSchema = z.object({
  fields: z
    .array(z.object({ key: z.string(), value: z.string() }))
    .min(1, 'Provide at least one field to update'),
});
export type UpdateNotificationChannelConfigRequest = z.infer<
  typeof UpdateNotificationChannelConfigRequestSchema
>;

/// Result of bouncing a handshake against the channel's upstream.
/// Stages:
///   - 'config'    : secrets missing / malformed - never opened a socket.
///   - 'handshake' : SMTP server returned an error during EHLO / AUTH.
///   - 'ready'     : transport reports ok=true; safe to send.
export const VerifyNotificationChannelResponseSchema = z.object({
  ok: z.boolean(),
  stage: z.enum(['config', 'handshake', 'ready']),
  host: z.string().nullable().optional(),
  port: z.number().int().nullable().optional(),
  from: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});
export type VerifyNotificationChannelResponse = z.infer<
  typeof VerifyNotificationChannelResponseSchema
>;
